// APIKeysPage replaces upstream's admin API keys page and its dialogs: one
// list that says what each key can do, when it expires and when it was last
// used, and a single dialog to create one (with templates) that ends on a
// copyable token and a ready-to-run curl line.
import React, { Suspense, useMemo, useState } from 'react'
import { gql, useMutation, useQuery } from 'urql'
import { DateTime } from 'luxon'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import {
  Add,
  ContentCopy,
  Delete,
  Edit,
  MoreHoriz,
  Replay,
  VpnKey,
  Visibility,
} from '@mui/icons-material'
import GraphQLEditor from '../editor/GraphQLEditor'
import { GenericError } from '../error-pages'
import { pathPrefix } from '../env'
import { ago } from './api'
import { Empty, IconTile, Page, Panel, Row, useBrand, useNow } from './ui'

// GoAlert's urql client suspends by default; this page renders its own states.
const noSuspense = { suspense: false }
const typenames = { additionalTypenames: ['GQLAPIKey'] }

const keysQuery = gql`
  query ErecordsAPIKeys {
    gqlAPIKeys {
      id
      name
      description
      createdAt
      createdBy {
        id
        name
      }
      lastUsed {
        time
        ua
        ip
      }
      expiresAt
      query
      role
    }
  }
`
const createMutation = gql`
  mutation ErecordsCreateAPIKey($input: CreateGQLAPIKeyInput!) {
    createGQLAPIKey(input: $input) {
      id
      token
    }
  }
`
const updateMutation = gql`
  mutation ErecordsUpdateAPIKey($input: UpdateGQLAPIKeyInput!) {
    updateGQLAPIKey(input: $input)
  }
`
const deleteMutation = gql`
  mutation ErecordsDeleteAPIKey($id: ID!) {
    deleteGQLAPIKey(id: $id)
  }
`

type Key = {
  id: string
  name: string
  description: string
  createdAt: string
  createdBy: { id: string; name: string } | null
  lastUsed: { time: string; ua: string; ip: string } | null
  expiresAt: string
  query: string
  role: 'user' | 'admin'
}

const gqlMessage = (e?: { message: string }): string =>
  (e?.message ?? '').replace('[GraphQL] ', '')

const changesData = (q: string): boolean => /^\s*mutation\b/m.test(q)

// a key's state: expired, expiring within two weeks, or fine
function expiry(
  k: Key,
  now: number,
): { label: string; tone: 'bad' | 'warn' | 'ok' } {
  const t = DateTime.fromISO(k.expiresAt)
  const days = t.diff(DateTime.fromMillis(now), 'days').days
  if (days < 0) return { label: `expired ${t.toRelative()}`, tone: 'bad' }
  if (days < 14) return { label: `expires ${t.toRelative()}`, tone: 'warn' }
  return { label: `expires ${t.toFormat('d LLL yyyy')}`, tone: 'ok' }
}

const templates: { label: string; query: string }[] = [
  {
    label: 'Raise an alert',
    query: `mutation ($summary: String!, $details: String, $serviceID: ID!) {
  createAlert(input: { summary: $summary, details: $details, serviceID: $serviceID }) {
    alertID
  }
}`,
  },
  {
    label: 'List open alerts',
    query: `query {
  alerts(input: { filterByStatus: [StatusUnacknowledged, StatusAcknowledged], first: 100 }) {
    nodes { alertID status summary createdAt service { name } }
  }
}`,
  },
  {
    label: 'Who is on call',
    query: `query {
  services(input: { first: 100 }) {
    nodes { name onCallUsers { userName stepNumber } }
  }
}`,
  },
]

const lifetimes = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 180, label: '6 months' },
  { days: 365, label: '1 year' },
]

function nextName(name: string): string {
  const m = name.match(/^(.*?)\s*(\d+)?$/)
  if (!m) return name
  return m[2] ? `${m[1]} ${parseInt(m[2], 10) + 1}` : `${m[1]} 2`
}

function Copy(props: { value: string; label?: string }): React.JSX.Element {
  const [done, setDone] = useState(false)
  return (
    <Button
      size='small'
      variant='outlined'
      startIcon={<ContentCopy fontSize='small' />}
      onClick={() =>
        navigator.clipboard.writeText(props.value).then(() => {
          setDone(true)
          setTimeout(() => setDone(false), 1500)
        })
      }
    >
      {done ? 'Copied' : (props.label ?? 'Copy')}
    </Button>
  )
}

function Code(props: { children: string }): React.JSX.Element {
  const c = useBrand()
  return (
    <Box
      component='pre'
      sx={{
        m: 0,
        p: 1.5,
        borderRadius: '10px',
        bgcolor: c.bg,
        border: `1px solid ${c.line}`,
        fontSize: 12.5,
        lineHeight: 1.5,
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        color: c.ink,
      }}
    >
      {props.children}
    </Box>
  )
}

function curlFor(token: string, query: string): string {
  // one placeholder per $variable, so the line runs once they are filled in
  const variables: Record<string, string> = {}
  query.replace(/\$(\w+)\s*:/g, (all, v: string) => {
    variables[v] = `<${v}>`
    return all
  })
  const body = JSON.stringify({
    query: query.replace(/\s+/g, ' ').trim(),
    ...(Object.keys(variables).length ? { variables } : {}),
  })
  return `curl -s ${window.location.origin}${pathPrefix}/api/graphql \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '${body.replace(/'/g, "'\\''")}'`
}

// ------------------------------------------------------------------ create

function CreateKeyDialog(props: {
  from?: Key // duplicate an existing key
  onClose: () => void
}): React.JSX.Element {
  const c = useBrand()
  const f = props.from
  const [m, create] = useMutation(createMutation)
  const [name, setName] = useState(f ? nextName(f.name) : '')
  const [description, setDescription] = useState(f?.description ?? '')
  const [role, setRole] = useState<'user' | 'admin'>(f?.role ?? 'user')
  const [days, setDays] = useState(() => {
    if (!f) return 90
    const d = Math.round(
      DateTime.fromISO(f.expiresAt).diff(DateTime.fromISO(f.createdAt), 'days')
        .days,
    )
    return lifetimes.some((l) => l.days === d) ? d : 90
  })
  const [query, setQuery] = useState(f?.query ?? templates[0].query)
  const token: string | undefined = m.data?.createGQLAPIKey?.token
  const valid = name.trim() && description.trim() && query.trim()

  if (token) {
    return (
      <Dialog open onClose={props.onClose} maxWidth='sm' fullWidth>
        <DialogTitle>Key created: {name.trim()}</DialogTitle>
        <DialogContent>
          <Alert severity='warning' sx={{ mb: 2 }}>
            Copy the token now. It is shown only once; if it is lost, create a
            new key.
          </Alert>
          <Box
            sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Code>{token}</Code>
            </Box>
            <Copy value={token} label='Copy token' />
          </Box>
          <Typography sx={{ fontWeight: 600, mb: 0.75 }}>Try it</Typography>
          <Code>{curlFor(token, query)}</Code>
          <Box sx={{ mt: 1 }}>
            <Copy value={curlFor(token, query)} label='Copy curl' />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant='contained' onClick={props.onClose}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog open onClose={props.onClose} maxWidth='md' fullWidth>
      <DialogTitle>{f ? `Duplicate “${f.name}”` : 'New API key'}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: c.muted, mb: 2 }}>
          A key runs one fixed GraphQL query or mutation, for a script or an
          integration. It cannot do anything else.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            mb: 2,
          }}
        >
          <TextField
            label='Name'
            placeholder='e.g. BVT reporter'
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label='What uses it?'
            placeholder='e.g. bvttest pipeline posts results'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            inputProps={{ maxLength: 255 }}
          />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            mb: 2.5,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Runs as</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {(['user', 'admin'] as const).map((r) => (
                <Chip
                  key={r}
                  label={r === 'user' ? 'User' : 'Admin'}
                  color={role === r ? 'primary' : 'default'}
                  variant={role === r ? 'filled' : 'outlined'}
                  onClick={() => setRole(r)}
                />
              ))}
            </Box>
            <Typography sx={{ fontSize: 12, color: c.muted, mt: 0.75 }}>
              {role === 'admin'
                ? 'Can run admin-only queries. Use only when User is not enough.'
                : 'Same rights as a normal user. Enough for most integrations.'}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Expires after
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {lifetimes.map((l) => (
                <Chip
                  key={l.days}
                  label={l.label}
                  color={days === l.days ? 'primary' : 'default'}
                  variant={days === l.days ? 'filled' : 'outlined'}
                  onClick={() => setDays(l.days)}
                />
              ))}
            </Box>
            <Typography sx={{ fontSize: 12, color: c.muted, mt: 0.75 }}>
              On {DateTime.now().plus({ days }).toFormat('d LLLL yyyy')}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontWeight: 600, mr: 1 }}>What it runs</Typography>
          {templates.map((t) => (
            <Chip
              key={t.label}
              size='small'
              label={t.label}
              variant={query === t.query ? 'filled' : 'outlined'}
              onClick={() => setQuery(t.query)}
            />
          ))}
        </Box>
        <Box
          sx={{
            border: `1px solid ${c.line}`,
            borderRadius: '10px',
            overflow: 'hidden',
          }}
        >
          <Suspense
            fallback={
              <Typography sx={{ p: 2, color: c.muted }}>
                Loading editor…
              </Typography>
            }
          >
            <GraphQLEditor
              value={query}
              onChange={setQuery}
              minHeight='12em'
              maxHeight='16em'
            />
          </Suspense>
        </Box>
        <Typography sx={{ fontSize: 12, color: c.muted, mt: 0.75 }}>
          {changesData(query)
            ? 'This changes data (a mutation).'
            : 'Read-only (a query).'}{' '}
          Variables are passed in each request.
        </Typography>

        {m.error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {gqlMessage(m.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          variant='contained'
          disabled={!valid || m.fetching}
          onClick={() =>
            create(
              {
                input: {
                  name: name.trim(),
                  description: description.trim(),
                  role,
                  query,
                  expiresAt: DateTime.utc().plus({ days }).toISO(),
                },
              },
              typenames,
            )
          }
        >
          Create key
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// -------------------------------------------------------------- view/edit

function ViewKeyDialog(props: {
  k: Key
  onClose: () => void
}): React.JSX.Element {
  const c = useBrand()
  const k = props.k
  const line = (label: string, value: React.ReactNode): React.JSX.Element => (
    <Box sx={{ display: 'flex', gap: 2, py: 0.5 }}>
      <Typography
        sx={{ width: 110, flexShrink: 0, fontSize: 13, color: c.muted }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, minWidth: 0 }}>{value}</Typography>
    </Box>
  )
  return (
    <Dialog open onClose={props.onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{k.name}</DialogTitle>
      <DialogContent>
        {line('Used for', k.description || '—')}
        {line('Runs as', k.role === 'admin' ? 'Admin' : 'User')}
        {line(
          'Created',
          `${DateTime.fromISO(k.createdAt).toFormat('d LLL yyyy')}${k.createdBy ? ` by ${k.createdBy.name}` : ''}`,
        )}
        {line('Expires', DateTime.fromISO(k.expiresAt).toFormat('d LLL yyyy'))}
        {line(
          'Last used',
          k.lastUsed
            ? `${DateTime.fromISO(k.lastUsed.time).toRelative()} from ${k.lastUsed.ip}`
            : 'never',
        )}
        {k.lastUsed?.ua && line('Client', k.lastUsed.ua)}
        <Typography sx={{ fontWeight: 600, mt: 2, mb: 0.75 }}>
          {changesData(k.query) ? 'Mutation it runs' : 'Query it runs'}
        </Typography>
        <Code>{k.query}</Code>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={props.onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

function EditKeyDialog(props: {
  k: Key
  onClose: () => void
}): React.JSX.Element {
  const c = useBrand()
  const [m, update] = useMutation(updateMutation)
  const [name, setName] = useState(props.k.name)
  const [description, setDescription] = useState(props.k.description)
  return (
    <Dialog open onClose={props.onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Edit API key</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label='Name'
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />
        <TextField
          fullWidth
          label='What uses it?'
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          inputProps={{ maxLength: 255 }}
        />
        <Typography sx={{ fontSize: 13, color: c.muted, mt: 1.5 }}>
          Role, expiry and the query cannot change. To change them, duplicate
          the key and delete this one.
        </Typography>
        {m.error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {gqlMessage(m.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          variant='contained'
          disabled={!name.trim() || !description.trim() || m.fetching}
          onClick={() =>
            update(
              {
                input: {
                  id: props.k.id,
                  name: name.trim(),
                  description: description.trim(),
                },
              },
              typenames,
            ).then((r) => !r.error && props.onClose())
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function DeleteKeyDialog(props: {
  k: Key
  onClose: () => void
}): React.JSX.Element {
  const [m, del] = useMutation(deleteMutation)
  return (
    <Dialog open onClose={props.onClose} maxWidth='xs' fullWidth>
      <DialogTitle>Delete “{props.k.name}”?</DialogTitle>
      <DialogContent>
        <Typography>
          Anything still using this key stops working right away.
        </Typography>
        {m.error && (
          <Alert severity='error' sx={{ mt: 2 }}>
            {gqlMessage(m.error)}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          variant='contained'
          color='error'
          disabled={m.fetching}
          onClick={() =>
            del({ id: props.k.id }, typenames).then(
              (r) => !r.error && props.onClose(),
            )
          }
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// -------------------------------------------------------------------- page

type Open =
  | { kind: 'create'; from?: Key }
  | { kind: 'view' | 'edit' | 'delete'; k: Key }

export default function APIKeysPage(): React.JSX.Element {
  const c = useBrand()
  const now = useNow()
  const [q] = useQuery({
    query: keysQuery,
    context: useMemo(() => ({ ...noSuspense, ...typenames }), []),
  })
  const [open, setOpen] = useState<Open | null>(null)
  const [menu, setMenu] = useState<{ el: HTMLElement; k: Key } | null>(null)

  if (q.error) return <GenericError error={q.error.message} />
  const keys: Key[] = (q.data?.gqlAPIKeys ?? [])
    .slice()
    .sort((a: Key, b: Key) =>
      a.name.localeCompare(b.name, undefined, { numeric: true }),
    )
  const close = (): void => setOpen(null)
  const pick = (o: Open) => (): void => {
    setMenu(null)
    setOpen(o)
  }
  const newButton = (
    <Button
      variant='contained'
      startIcon={<Add />}
      onClick={() => setOpen({ kind: 'create' })}
    >
      New API key
    </Button>
  )

  return (
    <Page
      title='API keys'
      subtitle='Keys let scripts and integrations run one fixed GraphQL query or mutation.'
      actions={keys.length ? newButton : undefined}
      maxWidth={1000}
    >
      <Panel flush>
        {!q.data && (
          <Typography sx={{ px: 2.5, py: 2, color: c.muted }}>
            Loading…
          </Typography>
        )}
        {q.data && keys.length === 0 && (
          <Empty
            title='No API keys yet'
            text='Create one for each script or integration, so each can be revoked on its own.'
            action={newButton}
          />
        )}
        {keys.map((k) => {
          const e = expiry(k, now)
          return (
            <Row
              key={k.id}
              leading={
                <IconTile tone={e.tone === 'bad' ? 'bad' : 'muted'}>
                  <VpnKey />
                </IconTile>
              }
              title={k.name}
              subtitle={[
                changesData(k.query) ? 'Changes data' : 'Read-only',
                k.role === 'admin' ? 'admin' : '',
                e.label,
                k.lastUsed
                  ? `last used ${ago(k.lastUsed.time, now)} ago`
                  : 'never used',
              ]
                .filter(Boolean)
                .join(' · ')}
              meta={
                e.tone !== 'ok' ? (
                  <Chip
                    size='small'
                    variant='outlined'
                    label={e.tone === 'bad' ? 'Expired' : 'Expires soon'}
                    color={e.tone === 'bad' ? 'error' : 'warning'}
                  />
                ) : undefined
              }
              trailing={
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button size='small' onClick={pick({ kind: 'view', k })}>
                    View
                  </Button>
                  <IconButton
                    size='small'
                    aria-label={`More for ${k.name}`}
                    onClick={(ev) => setMenu({ el: ev.currentTarget, k })}
                  >
                    <MoreHoriz fontSize='small' />
                  </IconButton>
                </Box>
              }
            />
          )
        })}
      </Panel>

      <Menu
        anchorEl={menu?.el}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {menu && [
          <MenuItem key='view' onClick={pick({ kind: 'view', k: menu.k })}>
            <ListItemIcon>
              <Visibility fontSize='small' />
            </ListItemIcon>
            View
          </MenuItem>,
          <MenuItem key='edit' onClick={pick({ kind: 'edit', k: menu.k })}>
            <ListItemIcon>
              <Edit fontSize='small' />
            </ListItemIcon>
            Edit name and purpose
          </MenuItem>,
          <MenuItem key='dup' onClick={pick({ kind: 'create', from: menu.k })}>
            <ListItemIcon>
              <Replay fontSize='small' />
            </ListItemIcon>
            Duplicate (new token)
          </MenuItem>,
          <MenuItem
            key='del'
            onClick={pick({ kind: 'delete', k: menu.k })}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <Delete fontSize='small' />
            </ListItemIcon>
            Delete
          </MenuItem>,
        ]}
      </Menu>

      {open?.kind === 'create' && (
        <CreateKeyDialog from={open.from} onClose={close} />
      )}
      {open?.kind === 'view' && <ViewKeyDialog k={open.k} onClose={close} />}
      {open?.kind === 'edit' && <EditKeyDialog k={open.k} onClose={close} />}
      {open?.kind === 'delete' && (
        <DeleteKeyDialog k={open.k} onClose={close} />
      )}
    </Page>
  )
}
